'use client';

import { Permission } from '@up2stay/up2promo-api-client';
import { Button, Skeleton } from '@up2stay/up2promo-ui';
import { ChevronRight, FileText, Inbox, Mail, Send } from 'lucide-react';
import { useMemo } from 'react';

import { FeedbackInviteListRow } from '@/core/components/feedback/feedback-invite-list-row';
import { FeedbackPageBreadcrumb } from '@/core/components/feedback/feedback-page-breadcrumb';
import { FeedbackPageShell } from '@/core/components/feedback/feedback-page-shell';
import { FEEDBACK_LIST_SURFACE_CLASS } from '@/core/components/feedback/feedback-surface';
import { ListEmptyState, ListPageBody } from '@/core/components/layout';
import {
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import { SettingsSection } from '@/core/components/settings/settings-section';
import {
  useFeedbackInvites,
  useFeedbackStats,
} from '@/core/hooks/use-feedback';
import { useLocale } from '@/core/hooks/use-locale';
import { useLocations } from '@/core/hooks/use-locations';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { usePermissions } from '@/core/hooks/use-permissions';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

export function FeedbackHomePage() {
  const dict = useDictionary();
  const feedbackDict = dict.feedback;
  const locale = useLocale();
  const { navigateShell } = useNavigateShell();
  const { can } = usePermissions();
  const canInvite = can(Permission.FEEDBACK_INVITE);
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const { locations } = useLocations();
  const { data: stats } = useFeedbackStats(workspaceId);
  const {
    data: invites = [],
    isLoading,
    error,
    refetch,
  } = useFeedbackInvites(workspaceId, { limit: 8 });

  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(locale === 'de' ? 'de-DE' : 'en-GB', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      }),
    [locale],
  );

  const locationName = (id: string) =>
    locations.find((l) => l.id === id)?.name ?? id;

  const inboxLabel =
    stats && stats.newCount > 0
      ? `${feedbackDict.home.openInbox} (${stats.newCount})`
      : feedbackDict.home.openInbox;

  return (
    <FeedbackPageShell
      title={feedbackDict.home.title}
      breadcrumb={
        <FeedbackPageBreadcrumb
          domainLabel={dict.nav.navDomainWork}
          pageLabel={dict.nav.feedback}
        />
      }
      actionsLayout="stack"
      actions={
        canInvite ? (
          <Button
            type="button"
            size="sm"
            onClick={() => navigateShell(shellPaths.feedbackCompose)}
          >
            <Send className="size-4" />
            {feedbackDict.home.askCta}
          </Button>
        ) : null
      }
    >
      <ListPageBody>
        <SettingsSection
          id="feedback-home-shortcuts"
          title={feedbackDict.home.heroTitle}
          grouped
        >
          <SettingsGroupRows>
            <SettingsGroupRow>
              <button
                type="button"
                className="flex w-full items-center justify-between gap-3 text-left"
                onClick={() => navigateShell(shellPaths.feedbackActivity)}
              >
                <span className="flex min-w-0 items-center gap-3">
                  <Inbox
                    className="size-4 shrink-0 text-muted-foreground"
                    aria-hidden
                  />
                  <span className="truncate text-[13.5px] font-medium text-foreground">
                    {inboxLabel}
                  </span>
                </span>
                <ChevronRight
                  className="size-4 shrink-0 text-muted-foreground"
                  aria-hidden
                />
              </button>
            </SettingsGroupRow>
            <SettingsGroupRow separated>
              <button
                type="button"
                className="flex w-full items-center justify-between gap-3 text-left"
                onClick={() => navigateShell(shellPaths.feedbackLibrary)}
              >
                <span className="flex min-w-0 items-center gap-3">
                  <FileText
                    className="size-4 shrink-0 text-muted-foreground"
                    aria-hidden
                  />
                  <span className="truncate text-[13.5px] font-medium text-foreground">
                    {feedbackDict.home.manageForms}
                  </span>
                </span>
                <ChevronRight
                  className="size-4 shrink-0 text-muted-foreground"
                  aria-hidden
                />
              </button>
            </SettingsGroupRow>
            <SettingsGroupRow separated>
              <button
                type="button"
                className="flex w-full items-center justify-between gap-3 text-left"
                onClick={() =>
                  navigateShell(`${shellPaths.feedbackLibrary}?tab=messages`)
                }
              >
                <span className="flex min-w-0 items-center gap-3">
                  <Mail
                    className="size-4 shrink-0 text-muted-foreground"
                    aria-hidden
                  />
                  <span className="truncate text-[13.5px] font-medium text-foreground">
                    {feedbackDict.home.manageMessages}
                  </span>
                </span>
                <ChevronRight
                  className="size-4 shrink-0 text-muted-foreground"
                  aria-hidden
                />
              </button>
            </SettingsGroupRow>
          </SettingsGroupRows>
        </SettingsSection>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2 px-1">
            <h2 className="text-[11px] font-semibold uppercase leading-none tracking-[0.6px] text-muted-foreground">
              {feedbackDict.home.recentTitle}
            </h2>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() =>
                navigateShell(`${shellPaths.feedbackActivity}?tab=sent`)
              }
            >
              {feedbackDict.home.viewAllSent}
            </Button>
          </div>

          {isLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-14 w-full rounded-[var(--r-card)]" />
              <Skeleton className="h-14 w-full rounded-[var(--r-card)]" />
            </div>
          ) : error ? (
            <ListEmptyState
              title={feedbackDict.invites.loadFailed}
              action={
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => refetch()}
                >
                  {feedbackDict.retry}
                </Button>
              }
            />
          ) : invites.length === 0 ? (
            <ListEmptyState
              icon={<Send className="h-6 w-6 text-foreground/70" />}
              title={feedbackDict.home.emptyRecentTitle}
              description={feedbackDict.home.emptyRecentDescription}
              action={
                canInvite ? (
                  <Button
                    type="button"
                    onClick={() => navigateShell(shellPaths.feedbackCompose)}
                  >
                    {feedbackDict.home.askCta}
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <ul className={FEEDBACK_LIST_SURFACE_CLASS}>
              {invites.map((invite) => (
                <FeedbackInviteListRow
                  key={invite.id}
                  invite={invite}
                  locationName={locationName(invite.locationId)}
                  dateLabel={dateFmt.format(new Date(invite.createdAt))}
                  copy={feedbackDict.invites.row}
                  onOpen={() =>
                    navigateShell(shellPaths.feedbackInviteDetail(invite.id))
                  }
                />
              ))}
            </ul>
          )}
        </div>
      </ListPageBody>
    </FeedbackPageShell>
  );
}
